import React, { useState, useEffect, useCallback } from 'react';
import {
  Grid,
  Card,
  CardContent,
  CardHeader,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  List,
  ListItem,
  ListItemText,
  Paper,
  Stack,
  useTheme,
  alpha,
  Container,
  Tooltip,
  IconButton,
} from '@mui/material';
import {
  AccountBalanceWallet,
  People,
  TrendingUp,
  TrendingDown,
  Casino,
  Refresh,
  DateRange,
  Analytics,
  MonetizationOn,
  SportsEsports,
  PersonAdd,
  SwapHoriz,
  Info,
  AttachMoney,
  FlightTakeoff,
  EmojiNature,
  Filter7,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format, differenceInDays } from 'date-fns';
import { useAuth } from '../../contexts/AuthContext';
import { reportsAPI } from '../../services/api';

const StatCard = ({ title, value, icon, color, subtitle, trend }) => {
  const theme = useTheme();
  
  return (
    <Card
      sx={{
        height: '100%',
        background: `linear-gradient(135deg, ${alpha(color, 0.05)} 0%, ${alpha(color, 0.02)} 100%)`,
        border: `1px solid ${alpha(color, 0.2)}`,
        borderLeft: `4px solid ${color}`,
        transition: 'all 0.2s',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: `0 8px 16px ${alpha(color, 0.15)}`,
        }
      }}
    >
      <CardContent sx={{ p: 2 }}>
        <Box display="flex" alignItems="flex-start" justifyContent="space-between">
          <Box flex={1}>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', fontSize: '0.7rem' }}>
              {title}
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 700, color: 'text.primary', my: 0.5, fontSize: '1.75rem' }}>
              {value}
            </Typography>
            {subtitle && (
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                {subtitle}
              </Typography>
            )}
            {trend !== undefined && trend !== null && (
              <Box display="flex" alignItems="center" gap={0.5} mt={1}>
                {trend >= 0 ? (
                  <TrendingUp sx={{ fontSize: 14, color: 'success.main' }} />
                ) : (
                  <TrendingDown sx={{ fontSize: 14, color: 'error.main' }} />
                )}
                <Typography variant="caption" color={trend >= 0 ? 'success.main' : 'error.main'} fontWeight="600">
                  {trend >= 0 ? '+' : ''}{trend} vs yesterday
                </Typography>
              </Box>
            )}
          </Box>
          <Box 
            sx={{ 
              color: color, 
              backgroundColor: alpha(color, 0.1),
              borderRadius: 2,
              p: 1,
            }}
          >
            {React.cloneElement(icon, { sx: { fontSize: 24 } })}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 2,
  }).format(amount || 0);
};

const Overview = () => {
  const { user } = useAuth();
  const theme = useTheme();
  
  const [quickStats, setQuickStats] = useState(null);
  const [dashboardOverview, setDashboardOverview] = useState(null);
  const [galiActivity, setGaliActivity] = useState({});
  const [matkaActivity, setMatkaActivity] = useState({});
  const [aviatorActivity, setAviatorActivity] = useState(null);
  const [chickenRoadActivity, setChickenRoadActivity] = useState(null);
  const [sevenUpDownActivity, setSevenUpDownActivity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dateRange, setDateRange] = useState({
    startDate: new Date(new Date().setDate(new Date().getDate() - 7)),
    endDate: new Date(),
  });
  const [refreshing, setRefreshing] = useState(false);

  const fetchDashboardData = useCallback(async (isRefresh = false) => {
    try {
      if (isRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      
      const [quickStatsRes, dashboardOverviewRes, galiActivityRes, matkaActivityRes, aviatorActivityRes, chickenRoadActivityRes, sevenUpDownActivityRes] = await Promise.all([
        reportsAPI.getQuickStats(),
        reportsAPI.getDashboardOverview(),
        reportsAPI.getGaliMarketActivity({
          start_date: format(dateRange.startDate, 'yyyy-MM-dd'),
          end_date: format(dateRange.endDate, 'yyyy-MM-dd'),
        }),
        reportsAPI.getMatkaMarketActivity({
          start_date: format(dateRange.startDate, 'yyyy-MM-dd'),
          end_date: format(dateRange.endDate, 'yyyy-MM-dd'),
        }),
        reportsAPI.getAviatorActivity({
          start_date: format(dateRange.startDate, 'yyyy-MM-dd'),
          end_date: format(dateRange.endDate, 'yyyy-MM-dd'),
        }),
        reportsAPI.getChickenRoadActivity({
          start_date: format(dateRange.startDate, 'yyyy-MM-dd'),
          end_date: format(dateRange.endDate, 'yyyy-MM-dd'),
        }),
        reportsAPI.getSevenUpDownActivity({
          start_date: format(dateRange.startDate, 'yyyy-MM-dd'),
          end_date: format(dateRange.endDate, 'yyyy-MM-dd'),
        }),
      ]);

      setQuickStats(quickStatsRes.data?.quick_stats || null);
      setDashboardOverview(dashboardOverviewRes.data?.dashboard_overview || null);
      setGaliActivity(galiActivityRes.data?.gali_market_activity || {});
      setMatkaActivity(matkaActivityRes.data?.matka_market_activity || {});
      setAviatorActivity(aviatorActivityRes.data || null);
      setChickenRoadActivity(chickenRoadActivityRes.data || null);
      setSevenUpDownActivity(sevenUpDownActivityRes.data || null);
      setError('');

    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      setError(`Failed to fetch dashboard data: ${error.response?.data?.message || error.message}`);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [dateRange]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  if (loading && !refreshing) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="400px">
        <CircularProgress size={50} />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="xl" sx={{ mt: 2 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  const handleRefresh = () => {
    fetchDashboardData(true);
  };

  const getDateRangeText = () => {
    const daysDiff = differenceInDays(dateRange.endDate, dateRange.startDate);
    if (daysDiff === 0) {
      return format(dateRange.startDate, 'MMM dd, yyyy');
    }
    return `${format(dateRange.startDate, 'MMM dd')} - ${format(dateRange.endDate, 'MMM dd, yyyy')}`;
  };

  // Calculate combined stats
  const users = quickStats?.users || {};
  const bets = quickStats?.bets || {};
  const transactions = quickStats?.transactions || {};
  const today = dashboardOverview?.today || {};
  const yesterday = dashboardOverview?.yesterday || {};
  const comparison = dashboardOverview?.comparison || {};

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Container maxWidth="xl" sx={{ py: 2 }}>
        
        {/* Header */}
        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Box>
            <Typography variant="h4" fontWeight="700" gutterBottom>
              Dashboard Overview
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {getDateRangeText()} • Welcome back, {user.username}
            </Typography>
          </Box>
          
          <Stack direction="row" spacing={1} flexWrap="wrap">
            <DatePicker
              label="Start Date"
              value={dateRange.startDate}
              onChange={(newValue) => newValue && setDateRange(prev => ({ ...prev, startDate: newValue }))}
              slotProps={{ textField: { size: 'small', sx: { width: 150 } } }}
            />
            <DatePicker
              label="End Date"
              value={dateRange.endDate}
              onChange={(newValue) => newValue && setDateRange(prev => ({ ...prev, endDate: newValue }))}
              slotProps={{ textField: { size: 'small', sx: { width: 150 } } }}
            />
            <Button 
              variant="contained" 
              onClick={handleRefresh} 
              disabled={refreshing}
              startIcon={refreshing ? <CircularProgress size={16} /> : <Refresh />}
              size="small"
            >
              {refreshing ? 'Refreshing...' : 'Refresh'}
            </Button>
          </Stack>
        </Box>

        {/* Quick Stats - Main Metrics */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Total Users"
              value={users.total_all || 0}
              subtitle={`Active: ${(users.active_gali_players || 0) + (users.active_matka_players || 0) + (users.active_aviator_players || 0) + (users.active_chicken_road_players || 0) + (users.active_seven_up_down_players || 0)}`}
              icon={<People />}
              color={theme.palette.info.main}
              trend={comparison.users?.new_users_change}
            />
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Total Bets"
              value={bets.total_all_bets || 0}
              subtitle={`Today: ${today.games?.total_bets || 0}`}
              icon={<Casino />}
              color={theme.palette.primary.main}
              trend={comparison.games?.total_bets_change}
            />
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Today's Inflow"
              value={formatCurrency(today.financials?.total_inflow || 0)}
              subtitle={`Deposits + Recharge`}
              icon={<MonetizationOn />}
              color={theme.palette.success.main}
              trend={comparison.financials?.deposits_change}
            />
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Pending Withdrawals"
              value={today.pending_withdrawals?.count || 0}
              subtitle={formatCurrency(today.pending_withdrawals?.amount || 0)}
              icon={<AccountBalanceWallet />}
              color={theme.palette.warning.main}
            />
          </Grid>
        </Grid>

        {/* Game Statistics Row */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={2.4}>
            <StatCard
              title="Gali Bets"
              value={bets.gali?.total_bets || 0}
              subtitle={`Today: ${today.games?.gali?.bets || 0}`}
              icon={<Casino />}
              color="#e91e63"
              trend={comparison.games?.gali_bets_change}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <StatCard
              title="Matka Bets"
              value={bets.matka?.total_bets || 0}
              subtitle={`Today: ${today.games?.matka?.bets || 0}`}
              icon={<SportsEsports />}
              color="#9c27b0"
              trend={comparison.games?.matka_bets_change}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <StatCard
              title="Aviator Bets"
              value={bets.aviator?.total_bets || 0}
              subtitle={`Profit: ${formatCurrency(bets.aviator?.profit || 0)}`}
              icon={<FlightTakeoff />}
              color="#ff5722"
              trend={comparison.games?.aviator_bets_change}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <StatCard
              title="Chicken Road"
              value={bets.chicken_road?.total_sessions || 0}
              subtitle={`Profit: ${formatCurrency(bets.chicken_road?.profit || 0)}`}
              icon={<EmojiNature />}
              color="#4caf50"
              trend={comparison.games?.chicken_road_sessions_change}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <StatCard
              title="7 Up Down"
              value={bets.seven_up_down?.total_bets || 0}
              subtitle={`Profit: ${formatCurrency(bets.seven_up_down?.profit || 0)}`}
              icon={<Filter7 />}
              color="#2196f3"
              trend={comparison.games?.seven_up_down_bets_change}
            />
          </Grid>
        </Grid>

        {/* Secondary Stats Row */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} md={8}>
            <Card sx={{ height: '100%' }}>
              <CardHeader 
                title={<Typography variant="h6" fontWeight="600">User Statistics</Typography>}
                avatar={<People color="primary" />}
                sx={{ pb: 1 }}
              />
              <CardContent sx={{ pt: 0 }}>
                <Grid container spacing={2}>
                  {users.total_by_type?.map((userType, index) => (
                    <Grid item xs={6} sm={4} md={3} key={index}>
                      <Paper sx={{ p: 2, textAlign: 'center', bgcolor: alpha(theme.palette.primary.main, 0.05) }}>
                        <Typography variant="h5" fontWeight="700" color="primary">
                          {userType.count}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'capitalize' }}>
                          {userType.user_type === 'super_admin' ? 'Super Admin' : userType.user_type}
                        </Typography>
                      </Paper>
                    </Grid>
                  ))}
                  <Grid item xs={6} sm={3}>
                    <Paper sx={{ p: 2, textAlign: 'center', bgcolor: alpha('#e91e63', 0.05) }}>
                      <Typography variant="h5" fontWeight="700" sx={{ color: '#e91e63' }}>
                        {users.active_gali_players || 0}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Gali Players
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Paper sx={{ p: 2, textAlign: 'center', bgcolor: alpha('#9c27b0', 0.05) }}>
                      <Typography variant="h5" fontWeight="700" sx={{ color: '#9c27b0' }}>
                        {users.active_matka_players || 0}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Matka Players
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Paper sx={{ p: 2, textAlign: 'center', bgcolor: alpha('#ff5722', 0.05) }}>
                      <Typography variant="h5" fontWeight="700" sx={{ color: '#ff5722' }}>
                        {users.active_aviator_players || 0}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Aviator Players
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Paper sx={{ p: 2, textAlign: 'center', bgcolor: alpha('#4caf50', 0.05) }}>
                      <Typography variant="h5" fontWeight="700" sx={{ color: '#4caf50' }}>
                        {users.active_chicken_road_players || 0}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Chicken Road Players
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Paper sx={{ p: 2, textAlign: 'center', bgcolor: alpha('#2196f3', 0.05) }}>
                      <Typography variant="h5" fontWeight="700" sx={{ color: '#2196f3' }}>
                        {users.active_seven_up_down_players || 0}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        7 Up Down Players
                      </Typography>
                    </Paper>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card sx={{ height: '100%' }}>
              <CardHeader 
                title={<Typography variant="h6" fontWeight="600">Bet Statistics</Typography>}
                avatar={<SportsEsports color="primary" />}
                sx={{ pb: 1 }}
              />
              <CardContent sx={{ pt: 0 }}>
                <Stack spacing={1.5}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2" color="text.secondary">Gali Bets</Typography>
                    <Chip label={bets.gali?.total_bets || 0} sx={{ bgcolor: alpha('#e91e63', 0.1), color: '#e91e63' }} size="small" />
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2" color="text.secondary">Matka Bets</Typography>
                    <Chip label={bets.matka?.total_bets || 0} sx={{ bgcolor: alpha('#9c27b0', 0.1), color: '#9c27b0' }} size="small" />
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2" color="text.secondary">Aviator Bets</Typography>
                    <Chip label={bets.aviator?.total_bets || 0} sx={{ bgcolor: alpha('#ff5722', 0.1), color: '#ff5722' }} size="small" />
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2" color="text.secondary">Chicken Road</Typography>
                    <Chip label={bets.chicken_road?.total_sessions || 0} sx={{ bgcolor: alpha('#4caf50', 0.1), color: '#4caf50' }} size="small" />
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2" color="text.secondary">7 Up Down</Typography>
                    <Chip label={bets.seven_up_down?.total_bets || 0} sx={{ bgcolor: alpha('#2196f3', 0.1), color: '#2196f3' }} size="small" />
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pt: 1, borderTop: '1px solid', borderColor: 'divider' }}>
                    <Typography variant="body2" fontWeight="600">Total All Bets</Typography>
                    <Chip label={bets.total_all_bets || 0} color="primary" size="small" />
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Profit Summary Card */}
        <Card sx={{ mb: 3 }}>
          <CardHeader 
            title={<Typography variant="h6" fontWeight="600">Profit Summary</Typography>}
            subheader={<Typography variant="caption" color="text.secondary">{quickStats?.profit_summary?.formula || '(Deposit + Recharge) - Withdrawal - Win'}</Typography>}
            avatar={<Analytics color="success" />}
          />
          <CardContent>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <Paper sx={{ p: 2, bgcolor: alpha(theme.palette.success.main, 0.05), border: `1px solid ${alpha(theme.palette.success.main, 0.2)}` }}>
                  <Typography variant="caption" color="text.secondary">Total Inflow</Typography>
                  <Typography variant="h5" fontWeight="700" color="success.main">
                    {formatCurrency(transactions.total_inflow || 0)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Deposits: {formatCurrency(transactions.total_deposits || 0)} + Recharge: {formatCurrency(transactions.total_recharge || 0)}
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Paper sx={{ p: 2, bgcolor: alpha(theme.palette.error.main, 0.05), border: `1px solid ${alpha(theme.palette.error.main, 0.2)}` }}>
                  <Typography variant="caption" color="text.secondary">Total Outflow</Typography>
                  <Typography variant="h5" fontWeight="700" color="error.main">
                    {formatCurrency(transactions.total_outflow || 0)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Withdrawals: {formatCurrency(transactions.total_withdrawals || 0)} + Wins: {formatCurrency(transactions.total_wins || 0)}
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Paper sx={{ p: 2, bgcolor: alpha(theme.palette.primary.main, 0.05), border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}` }}>
                  <Typography variant="caption" color="text.secondary">Net Profit</Typography>
                  <Typography variant="h5" fontWeight="700" color={transactions.net_profit >= 0 ? 'success.main' : 'error.main'}>
                    {formatCurrency(transactions.net_profit || 0)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Inflow - Outflow
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Paper sx={{ p: 2, bgcolor: alpha(theme.palette.info.main, 0.05), border: `1px solid ${alpha(theme.palette.info.main, 0.2)}` }}>
                  <Typography variant="caption" color="text.secondary">Today's Net Profit</Typography>
                  <Typography variant="h5" fontWeight="700" color={today.financials?.net_profit >= 0 ? 'success.main' : 'error.main'}>
                    {formatCurrency(today.financials?.net_profit || 0)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Change: {formatCurrency(comparison.financials?.net_profit_change || 0)}
                  </Typography>
                </Paper>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Transaction Summary */}
        <Card sx={{ mb: 3 }}>
          <CardHeader 
            title={<Typography variant="h6" fontWeight="600">Transaction Summary</Typography>}
            avatar={<AttachMoney color="success" />}
          />
          <CardContent>
            <Grid container spacing={2}>
              {transactions.by_type?.map((txn, index) => (
                <Grid item xs={12} sm={4} key={index}>
                  <Paper 
                    sx={{ 
                      p: 2, 
                      bgcolor: alpha(
                        txn.transaction_type === 'deposit' ? theme.palette.success.main :
                        txn.transaction_type === 'withdrawal' ? theme.palette.error.main :
                        theme.palette.info.main, 
                        0.05
                      ),
                      border: `1px solid ${alpha(
                        txn.transaction_type === 'deposit' ? theme.palette.success.main :
                        txn.transaction_type === 'withdrawal' ? theme.palette.error.main :
                        theme.palette.info.main, 
                        0.2
                      )}`
                    }}
                  >
                    <Stack spacing={0.5}>
                      <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'capitalize' }}>
                        {txn.transaction_type}
                      </Typography>
                      <Typography variant="h5" fontWeight="700">
                        {formatCurrency(txn.total_amount)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Count: {txn.count}
                      </Typography>
                    </Stack>
                  </Paper>
                </Grid>
              ))}
              <Grid item xs={12} sm={4}>
                <Paper 
                  sx={{ 
                    p: 2, 
                    bgcolor: alpha(theme.palette.primary.main, 0.05),
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
                  }}
                >
                  <Stack spacing={0.5}>
                    <Typography variant="caption" color="text.secondary">
                      Net Flow
                    </Typography>
                    <Typography variant="h5" fontWeight="700" color="primary">
                      {formatCurrency(transactions.net_flow || 0)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Deposits - Withdrawals
                    </Typography>
                  </Stack>
                </Paper>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Today vs Yesterday Comparison */}
        <Card sx={{ mb: 3 }}>
          <CardHeader 
            title={<Typography variant="h6" fontWeight="600">Today vs Yesterday</Typography>}
            avatar={<Analytics color="primary" />}
          />
          <CardContent>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Metric</strong></TableCell>
                    <TableCell align="right"><strong>Today</strong></TableCell>
                    <TableCell align="right"><strong>Yesterday</strong></TableCell>
                    <TableCell align="right"><strong>Change</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell>Gali Bets</TableCell>
                    <TableCell align="right">{today.games?.gali?.bets || 0}</TableCell>
                    <TableCell align="right">{yesterday.games?.gali?.bets || 0}</TableCell>
                    <TableCell align="right">
                      <Chip 
                        label={`${comparison.games?.gali_bets_change >= 0 ? '+' : ''}${comparison.games?.gali_bets_change || 0}`}
                        size="small"
                        color={(comparison.games?.gali_bets_change || 0) >= 0 ? 'success' : 'error'}
                      />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Matka Bets</TableCell>
                    <TableCell align="right">{today.games?.matka?.bets || 0}</TableCell>
                    <TableCell align="right">{yesterday.games?.matka?.bets || 0}</TableCell>
                    <TableCell align="right">
                      <Chip 
                        label={`${comparison.games?.matka_bets_change >= 0 ? '+' : ''}${comparison.games?.matka_bets_change || 0}`}
                        size="small"
                        color={(comparison.games?.matka_bets_change || 0) >= 0 ? 'success' : 'error'}
                      />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Aviator Bets</TableCell>
                    <TableCell align="right">{today.games?.aviator?.bets || 0}</TableCell>
                    <TableCell align="right">{yesterday.games?.aviator?.bets || 0}</TableCell>
                    <TableCell align="right">
                      <Chip 
                        label={`${comparison.games?.aviator_bets_change >= 0 ? '+' : ''}${comparison.games?.aviator_bets_change || 0}`}
                        size="small"
                        color={(comparison.games?.aviator_bets_change || 0) >= 0 ? 'success' : 'error'}
                      />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Chicken Road Sessions</TableCell>
                    <TableCell align="right">{today.games?.chicken_road?.sessions || 0}</TableCell>
                    <TableCell align="right">{yesterday.games?.chicken_road?.sessions || 0}</TableCell>
                    <TableCell align="right">
                      <Chip 
                        label={`${comparison.games?.chicken_road_sessions_change >= 0 ? '+' : ''}${comparison.games?.chicken_road_sessions_change || 0}`}
                        size="small"
                        color={(comparison.games?.chicken_road_sessions_change || 0) >= 0 ? 'success' : 'error'}
                      />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>7 Up Down Bets</TableCell>
                    <TableCell align="right">{today.games?.seven_up_down?.bets || 0}</TableCell>
                    <TableCell align="right">{yesterday.games?.seven_up_down?.bets || 0}</TableCell>
                    <TableCell align="right">
                      <Chip 
                        label={`${comparison.games?.seven_up_down_bets_change >= 0 ? '+' : ''}${comparison.games?.seven_up_down_bets_change || 0}`}
                        size="small"
                        color={(comparison.games?.seven_up_down_bets_change || 0) >= 0 ? 'success' : 'error'}
                      />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>New Users</TableCell>
                    <TableCell align="right">{today.users?.new_users || 0}</TableCell>
                    <TableCell align="right">{yesterday.users?.new_users || 0}</TableCell>
                    <TableCell align="right">
                      <Chip 
                        label={`${comparison.users?.new_users_change >= 0 ? '+' : ''}${comparison.users?.new_users_change || 0}`}
                        size="small"
                        color={(comparison.users?.new_users_change || 0) >= 0 ? 'success' : 'error'}
                      />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Deposits</TableCell>
                    <TableCell align="right">{formatCurrency(today.financials?.deposits || 0)}</TableCell>
                    <TableCell align="right">{formatCurrency(yesterday.financials?.deposits || 0)}</TableCell>
                    <TableCell align="right">
                      <Chip 
                        label={`${comparison.financials?.deposits_change >= 0 ? '+' : ''}${formatCurrency(comparison.financials?.deposits_change || 0)}`}
                        size="small"
                        color={(comparison.financials?.deposits_change || 0) >= 0 ? 'success' : 'error'}
                      />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Recharge</TableCell>
                    <TableCell align="right">{formatCurrency(today.financials?.recharge || 0)}</TableCell>
                    <TableCell align="right">{formatCurrency(yesterday.financials?.recharge || 0)}</TableCell>
                    <TableCell align="right">
                      <Chip 
                        label={`${comparison.financials?.recharge_change >= 0 ? '+' : ''}${formatCurrency(comparison.financials?.recharge_change || 0)}`}
                        size="small"
                        color={(comparison.financials?.recharge_change || 0) >= 0 ? 'success' : 'error'}
                      />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Withdrawals</TableCell>
                    <TableCell align="right">{formatCurrency(today.financials?.withdrawals || 0)}</TableCell>
                    <TableCell align="right">{formatCurrency(yesterday.financials?.withdrawals || 0)}</TableCell>
                    <TableCell align="right">
                      <Chip 
                        label={`${comparison.financials?.withdrawals_change >= 0 ? '+' : ''}${formatCurrency(comparison.financials?.withdrawals_change || 0)}`}
                        size="small"
                        color={(comparison.financials?.withdrawals_change || 0) <= 0 ? 'success' : 'error'}
                      />
                    </TableCell>
                  </TableRow>
                  <TableRow sx={{ bgcolor: alpha(theme.palette.primary.main, 0.05) }}>
                    <TableCell><strong>Net Profit</strong></TableCell>
                    <TableCell align="right"><strong>{formatCurrency(today.financials?.net_profit || 0)}</strong></TableCell>
                    <TableCell align="right"><strong>{formatCurrency(yesterday.financials?.net_profit || 0)}</strong></TableCell>
                    <TableCell align="right">
                      <Chip 
                        label={`${comparison.financials?.net_profit_change >= 0 ? '+' : ''}${formatCurrency(comparison.financials?.net_profit_change || 0)}`}
                        size="small"
                        color={(comparison.financials?.net_profit_change || 0) >= 0 ? 'success' : 'error'}
                      />
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>

        {/* Market Activity */}
        <Grid container spacing={2}>
          {/* Gali Markets */}
          <Grid item xs={12} lg={6}>
            <Card>
              <CardHeader 
                title={<Typography variant="h6" fontWeight="600">Gali Market Activity</Typography>}
                avatar={<Casino sx={{ color: '#e91e63' }} />}
              />
              <CardContent sx={{ p: 0 }}>
                {Object.keys(galiActivity).length > 0 ? (
                  <TableContainer sx={{ maxHeight: 400 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell><strong>Market</strong></TableCell>
                          <TableCell align="right"><strong>Bets</strong></TableCell>
                          <TableCell align="right"><strong>Amount</strong></TableCell>
                          <TableCell align="right"><strong>P/L</strong></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {Object.entries(galiActivity).map(([date, markets]) =>
                          Object.entries(markets).map(([marketId, market]) => (
                            <TableRow key={`${date}-${marketId}`} hover>
                              <TableCell>
                                <Typography variant="body2" fontWeight="500">{market.market_name}</Typography>
                                <Typography variant="caption" color="text.secondary">{date}</Typography>
                              </TableCell>
                              <TableCell align="right">
                                <Tooltip title={`Pending: ${market.pending_bets}`}>
                                  <span>{market.total_bets}</span>
                                </Tooltip>
                              </TableCell>
                              <TableCell align="right">
                                <Typography variant="body2" color="primary">
                                  {formatCurrency(market.total_bet_amount)}
                                </Typography>
                              </TableCell>
                              <TableCell align="right">
                                <Typography 
                                  variant="body2" 
                                  fontWeight="600"
                                  color={market.profit_loss >= 0 ? 'success.main' : 'error.main'}
                                >
                                  {formatCurrency(market.profit_loss)}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Box p={3} textAlign="center">
                    <Typography variant="body2" color="text.secondary">No Gali market activity</Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Matka Markets */}
          <Grid item xs={12} lg={6}>
            <Card>
              <CardHeader 
                title={<Typography variant="h6" fontWeight="600">Matka Market Activity</Typography>}
                avatar={<SportsEsports sx={{ color: '#9c27b0' }} />}
              />
              <CardContent sx={{ p: 0 }}>
                {Object.keys(matkaActivity).length > 0 ? (
                  <TableContainer sx={{ maxHeight: 400 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell><strong>Market</strong></TableCell>
                          <TableCell align="right"><strong>Bets</strong></TableCell>
                          <TableCell align="right"><strong>Amount</strong></TableCell>
                          <TableCell align="right"><strong>P/L</strong></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {Object.entries(matkaActivity).map(([date, markets]) =>
                          Object.entries(markets).map(([marketId, market]) => (
                            <TableRow key={`${date}-${marketId}`} hover>
                              <TableCell>
                                <Typography variant="body2" fontWeight="500">{market.market_name}</Typography>
                                <Typography variant="caption" color="text.secondary">{date}</Typography>
                              </TableCell>
                              <TableCell align="right">
                                <Tooltip title={`Pending: ${market.pending_bets}`}>
                                  <span>{market.total_bets}</span>
                                </Tooltip>
                              </TableCell>
                              <TableCell align="right">
                                <Typography variant="body2" sx={{ color: '#9c27b0' }}>
                                  {formatCurrency(market.total_bet_amount)}
                                </Typography>
                              </TableCell>
                              <TableCell align="right">
                                <Typography 
                                  variant="body2" 
                                  fontWeight="600"
                                  color={market.profit_loss >= 0 ? 'success.main' : 'error.main'}
                                >
                                  {formatCurrency(market.profit_loss)}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Box p={3} textAlign="center">
                    <Typography variant="body2" color="text.secondary">No Matka market activity</Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Aviator & Chicken Road Activity */}
        <Grid container spacing={2} sx={{ mt: 2 }}>
          {/* Aviator Activity */}
          <Grid item xs={12} lg={6}>
            <Card>
              <CardHeader 
                title={<Typography variant="h6" fontWeight="600">Aviator Activity</Typography>}
                subheader={aviatorActivity?.summary && (
                  <Typography variant="caption" color="text.secondary">
                    RTP: {aviatorActivity.summary.rtp} | House Profit: {formatCurrency(aviatorActivity.summary.house_profit)}
                  </Typography>
                )}
                avatar={<FlightTakeoff sx={{ color: '#ff5722' }} />}
              />
              <CardContent sx={{ p: 0 }}>
                {aviatorActivity?.daily_activity && Object.keys(aviatorActivity.daily_activity).length > 0 ? (
                  <>
                    {/* Summary Stats */}
                    <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
                      <Grid container spacing={2}>
                        <Grid item xs={6} sm={3}>
                          <Typography variant="caption" color="text.secondary">Total Bets</Typography>
                          <Typography variant="h6" fontWeight="600">{aviatorActivity.summary?.total_bets || 0}</Typography>
                        </Grid>
                        <Grid item xs={6} sm={3}>
                          <Typography variant="caption" color="text.secondary">Total Wagered</Typography>
                          <Typography variant="h6" fontWeight="600" color="primary">{formatCurrency(aviatorActivity.summary?.total_wagered || 0)}</Typography>
                        </Grid>
                        <Grid item xs={6} sm={3}>
                          <Typography variant="caption" color="text.secondary">Total Payout</Typography>
                          <Typography variant="h6" fontWeight="600" color="error.main">{formatCurrency(aviatorActivity.summary?.total_payout || 0)}</Typography>
                        </Grid>
                        <Grid item xs={6} sm={3}>
                          <Typography variant="caption" color="text.secondary">House Profit</Typography>
                          <Typography variant="h6" fontWeight="600" color="success.main">{formatCurrency(aviatorActivity.summary?.house_profit || 0)}</Typography>
                        </Grid>
                      </Grid>
                    </Box>
                    <TableContainer sx={{ maxHeight: 300 }}>
                      <Table size="small" stickyHeader>
                        <TableHead>
                          <TableRow>
                            <TableCell><strong>Date</strong></TableCell>
                            <TableCell align="right"><strong>Bets</strong></TableCell>
                            <TableCell align="right"><strong>Wagered</strong></TableCell>
                            <TableCell align="right"><strong>Payout</strong></TableCell>
                            <TableCell align="right"><strong>Profit</strong></TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {Object.entries(aviatorActivity.daily_activity).map(([date, data]) => (
                            <TableRow key={date} hover>
                              <TableCell>{date}</TableCell>
                              <TableCell align="right">
                                <Tooltip title={`Cashed: ${data.cashed_out} | Lost: ${data.lost} | Open: ${data.open}`}>
                                  <span>{data.total_bets}</span>
                                </Tooltip>
                              </TableCell>
                              <TableCell align="right">{formatCurrency(data.total_bet_amount)}</TableCell>
                              <TableCell align="right">{formatCurrency(data.total_payout)}</TableCell>
                              <TableCell align="right">
                                <Typography 
                                  variant="body2" 
                                  fontWeight="600"
                                  color={data.house_profit >= 0 ? 'success.main' : 'error.main'}
                                >
                                  {formatCurrency(data.house_profit)}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </>
                ) : (
                  <Box p={3} textAlign="center">
                    <Typography variant="body2" color="text.secondary">No Aviator activity</Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Chicken Road Activity */}
          <Grid item xs={12} lg={6}>
            <Card>
              <CardHeader 
                title={<Typography variant="h6" fontWeight="600">Chicken Road Activity</Typography>}
                subheader={chickenRoadActivity?.summary && (
                  <Typography variant="caption" color="text.secondary">
                    Win Rate: {chickenRoadActivity.summary.win_rate} | RTP: {chickenRoadActivity.summary.rtp}
                  </Typography>
                )}
                avatar={<EmojiNature sx={{ color: '#4caf50' }} />}
              />
              <CardContent sx={{ p: 0 }}>
                {chickenRoadActivity?.daily_activity && Object.keys(chickenRoadActivity.daily_activity).length > 0 ? (
                  <>
                    {/* Summary Stats */}
                    <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
                      <Grid container spacing={2}>
                        <Grid item xs={6} sm={3}>
                          <Typography variant="caption" color="text.secondary">Total Sessions</Typography>
                          <Typography variant="h6" fontWeight="600">{chickenRoadActivity.summary?.total_sessions || 0}</Typography>
                        </Grid>
                        <Grid item xs={6} sm={3}>
                          <Typography variant="caption" color="text.secondary">Total Wagered</Typography>
                          <Typography variant="h6" fontWeight="600" color="primary">{formatCurrency(chickenRoadActivity.summary?.total_wagered || 0)}</Typography>
                        </Grid>
                        <Grid item xs={6} sm={3}>
                          <Typography variant="caption" color="text.secondary">Won / Lost</Typography>
                          <Typography variant="h6" fontWeight="600">
                            <span style={{ color: '#4caf50' }}>{chickenRoadActivity.summary?.won_sessions || 0}</span>
                            {' / '}
                            <span style={{ color: '#f44336' }}>{chickenRoadActivity.summary?.lost_sessions || 0}</span>
                          </Typography>
                        </Grid>
                        <Grid item xs={6} sm={3}>
                          <Typography variant="caption" color="text.secondary">House Profit</Typography>
                          <Typography variant="h6" fontWeight="600" color="success.main">{formatCurrency(chickenRoadActivity.summary?.house_profit || 0)}</Typography>
                        </Grid>
                      </Grid>
                    </Box>
                    <TableContainer sx={{ maxHeight: 300 }}>
                      <Table size="small" stickyHeader>
                        <TableHead>
                          <TableRow>
                            <TableCell><strong>Date</strong></TableCell>
                            <TableCell align="right"><strong>Sessions</strong></TableCell>
                            <TableCell align="right"><strong>Wagered</strong></TableCell>
                            <TableCell align="right"><strong>Payout</strong></TableCell>
                            <TableCell align="right"><strong>Profit</strong></TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {Object.entries(chickenRoadActivity.daily_activity).map(([date, data]) => (
                            <TableRow key={date} hover>
                              <TableCell>{date}</TableCell>
                              <TableCell align="right">
                                <Tooltip title={`Won: ${data.won} | Lost: ${data.lost} | Active: ${data.active}`}>
                                  <span>{data.total_sessions}</span>
                                </Tooltip>
                              </TableCell>
                              <TableCell align="right">{formatCurrency(data.total_bet_amount)}</TableCell>
                              <TableCell align="right">{formatCurrency(data.total_payout)}</TableCell>
                              <TableCell align="right">
                                <Typography 
                                  variant="body2" 
                                  fontWeight="600"
                                  color={data.house_profit >= 0 ? 'success.main' : 'error.main'}
                                >
                                  {formatCurrency(data.house_profit)}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </>
                ) : (
                  <Box p={3} textAlign="center">
                    <Typography variant="body2" color="text.secondary">No Chicken Road activity</Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* 7 Up Down Activity */}
        <Grid container spacing={2} sx={{ mt: 2 }}>
          <Grid item xs={12}>
            <Card>
              <CardHeader 
                title={<Typography variant="h6" fontWeight="600">7 Up Down Activity</Typography>}
                subheader={sevenUpDownActivity?.summary && (
                  <Typography variant="caption" color="text.secondary">
                    Win Rate: {sevenUpDownActivity.summary.win_rate} | RTP: {sevenUpDownActivity.summary.rtp} | Rounds: {sevenUpDownActivity.summary.rounds_played}
                  </Typography>
                )}
                avatar={<Filter7 sx={{ color: '#2196f3' }} />}
              />
              <CardContent sx={{ p: 0 }}>
                {sevenUpDownActivity?.daily_activity && Object.keys(sevenUpDownActivity.daily_activity).length > 0 ? (
                  <>
                    {/* Summary Stats */}
                    <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
                      <Grid container spacing={2}>
                        <Grid item xs={6} sm={2.4}>
                          <Typography variant="caption" color="text.secondary">Total Bets</Typography>
                          <Typography variant="h6" fontWeight="600">{sevenUpDownActivity.summary?.total_bets || 0}</Typography>
                        </Grid>
                        <Grid item xs={6} sm={2.4}>
                          <Typography variant="caption" color="text.secondary">Total Wagered</Typography>
                          <Typography variant="h6" fontWeight="600" color="primary">{formatCurrency(sevenUpDownActivity.summary?.total_wagered || 0)}</Typography>
                        </Grid>
                        <Grid item xs={6} sm={2.4}>
                          <Typography variant="caption" color="text.secondary">Total Payout</Typography>
                          <Typography variant="h6" fontWeight="600" color="error.main">{formatCurrency(sevenUpDownActivity.summary?.total_payout || 0)}</Typography>
                        </Grid>
                        <Grid item xs={6} sm={2.4}>
                          <Typography variant="caption" color="text.secondary">Won / Lost</Typography>
                          <Typography variant="h6" fontWeight="600">
                            <span style={{ color: '#4caf50' }}>{sevenUpDownActivity.summary?.won_bets || 0}</span>
                            {' / '}
                            <span style={{ color: '#f44336' }}>{sevenUpDownActivity.summary?.lost_bets || 0}</span>
                          </Typography>
                        </Grid>
                        <Grid item xs={6} sm={2.4}>
                          <Typography variant="caption" color="text.secondary">House Profit</Typography>
                          <Typography variant="h6" fontWeight="600" color="success.main">{formatCurrency(sevenUpDownActivity.summary?.house_profit || 0)}</Typography>
                        </Grid>
                      </Grid>
                    </Box>
                    <TableContainer sx={{ maxHeight: 400 }}>
                      <Table size="small" stickyHeader>
                        <TableHead>
                          <TableRow>
                            <TableCell><strong>Date</strong></TableCell>
                            <TableCell align="right"><strong>Bets</strong></TableCell>
                            <TableCell align="right"><strong>Wagered</strong></TableCell>
                            <TableCell align="right"><strong>Payout</strong></TableCell>
                            <TableCell align="center"><strong>DOWN</strong></TableCell>
                            <TableCell align="center"><strong>SEVEN</strong></TableCell>
                            <TableCell align="center"><strong>UP</strong></TableCell>
                            <TableCell align="right"><strong>Profit</strong></TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {Object.entries(sevenUpDownActivity.daily_activity).map(([date, data]) => (
                            <TableRow key={date} hover>
                              <TableCell>{date}</TableCell>
                              <TableCell align="right">
                                <Tooltip title={`Won: ${data.won} | Lost: ${data.lost} | Pending: ${data.pending}`}>
                                  <span>{data.total_bets}</span>
                                </Tooltip>
                              </TableCell>
                              <TableCell align="right">{formatCurrency(data.total_bet_amount)}</TableCell>
                              <TableCell align="right">{formatCurrency(data.total_payout)}</TableCell>
                              <TableCell align="center">
                                <Tooltip title={`Amount: ${formatCurrency(data.by_option?.DOWN?.amount || 0)} | Won: ${data.by_option?.DOWN?.won || 0}`}>
                                  <Chip label={data.by_option?.DOWN?.bets || 0} size="small" sx={{ bgcolor: alpha('#f44336', 0.1), color: '#f44336' }} />
                                </Tooltip>
                              </TableCell>
                              <TableCell align="center">
                                <Tooltip title={`Amount: ${formatCurrency(data.by_option?.SEVEN?.amount || 0)} | Won: ${data.by_option?.SEVEN?.won || 0}`}>
                                  <Chip label={data.by_option?.SEVEN?.bets || 0} size="small" sx={{ bgcolor: alpha('#9c27b0', 0.1), color: '#9c27b0' }} />
                                </Tooltip>
                              </TableCell>
                              <TableCell align="center">
                                <Tooltip title={`Amount: ${formatCurrency(data.by_option?.UP?.amount || 0)} | Won: ${data.by_option?.UP?.won || 0}`}>
                                  <Chip label={data.by_option?.UP?.bets || 0} size="small" sx={{ bgcolor: alpha('#4caf50', 0.1), color: '#4caf50' }} />
                                </Tooltip>
                              </TableCell>
                              <TableCell align="right">
                                <Typography 
                                  variant="body2" 
                                  fontWeight="600"
                                  color={data.house_profit >= 0 ? 'success.main' : 'error.main'}
                                >
                                  {formatCurrency(data.house_profit)}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </>
                ) : (
                  <Box p={3} textAlign="center">
                    <Typography variant="body2" color="text.secondary">No 7 Up Down activity</Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Detailed Matka Breakdown - Card Design */}
        {Object.keys(matkaActivity).length > 0 && (
          <Card sx={{ mt: 2 }}>
            <CardHeader 
              title={<Typography variant="h6" fontWeight="600">Matka Bet Type & Game Mode Breakdown</Typography>}
              avatar={<Info color="info" />}
            />
            <CardContent>
              {Object.entries(matkaActivity).map(([date, markets]) =>
                Object.entries(markets).map(([marketId, market]) => (
                  market.bet_type_breakdown && (
                    <Box key={`${date}-${marketId}-breakdown`} sx={{ mb: 4 }}>
                      <Paper 
                        elevation={0}
                        sx={{ 
                          p: 2, 
                          mb: 2, 
                          bgcolor: alpha(theme.palette.primary.main, 0.05),
                          border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
                        }}
                      >
                        <Typography variant="subtitle1" fontWeight="700" color="primary">
                          {market.market_name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {date}
                        </Typography>
                      </Paper>

                      {/* Bet Types */}
                      <Box sx={{ mb: 3 }}>
                        <Typography 
                          variant="caption" 
                          color="text.secondary" 
                          fontWeight="700" 
                          sx={{ mb: 1.5, display: 'block', textTransform: 'uppercase', letterSpacing: 1 }}
                        >
                          Bet Types
                        </Typography>
                        <Grid container spacing={1.5}>
                          {Object.entries(market.bet_type_breakdown).map(([betType, data]) => (
                            <Grid item xs={12} sm={6} md={4} lg={3} key={betType}>
                              <Paper
                                elevation={0}
                                sx={{
                                  p: 1.5,
                                  border: `1px solid ${alpha(theme.palette.divider, 0.3)}`,
                                  borderLeft: `3px solid ${theme.palette.primary.main}`,
                                  transition: 'all 0.2s',
                                  '&:hover': {
                                    transform: 'translateY(-2px)',
                                    boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.1)}`,
                                    borderLeftColor: theme.palette.secondary.main,
                                  }
                                }}
                              >
                                <Typography 
                                  variant="caption" 
                                  fontWeight="600" 
                                  color="text.primary"
                                  sx={{ 
                                    display: 'block', 
                                    mb: 1,
                                    fontSize: '0.7rem',
                                    textTransform: 'uppercase',
                                    letterSpacing: 0.5
                                  }}
                                >
                                  {betType}
                                </Typography>
                                <Stack spacing={0.5}>
                                  <Box display="flex" justifyContent="space-between" alignItems="center">
                                    <Typography variant="caption" color="text.secondary" fontSize="0.65rem">
                                      Bets
                                    </Typography>
                                    <Chip 
                                      label={data.bets} 
                                      size="small" 
                                      sx={{ 
                                        height: 18, 
                                        fontSize: '0.65rem',
                                        fontWeight: 600,
                                        bgcolor: alpha(theme.palette.primary.main, 0.1),
                                        color: 'primary.main'
                                      }} 
                                    />
                                  </Box>
                                  <Box display="flex" justifyContent="space-between" alignItems="center">
                                    <Typography variant="caption" color="text.secondary" fontSize="0.65rem">
                                      Amount
                                    </Typography>
                                    <Typography variant="caption" fontWeight="700" color="success.main" fontSize="0.7rem">
                                      {formatCurrency(data.amount)}
                                    </Typography>
                                  </Box>
                                  <Box display="flex" justifyContent="space-between" alignItems="center">
                                    <Typography variant="caption" color="text.secondary" fontSize="0.65rem">
                                      Wins
                                    </Typography>
                                    <Chip 
                                      label={data.wins} 
                                      size="small" 
                                      color={data.wins > 0 ? "error" : "default"}
                                      sx={{ 
                                        height: 18, 
                                        fontSize: '0.65rem',
                                        fontWeight: 600
                                      }} 
                                    />
                                  </Box>
                                </Stack>
                              </Paper>
                            </Grid>
                          ))}
                        </Grid>
                      </Box>

                      {/* Game Modes */}
                      {market.game_mode_breakdown && (
                        <Box>
                          <Typography 
                            variant="caption" 
                            color="text.secondary" 
                            fontWeight="700" 
                            sx={{ mb: 1.5, display: 'block', textTransform: 'uppercase', letterSpacing: 1 }}
                          >
                            Game Modes
                          </Typography>
                          <Grid container spacing={1.5}>
                            {Object.entries(market.game_mode_breakdown).map(([mode, data]) => (
                              <Grid item xs={12} sm={6} md={4} lg={2.4} key={mode}>
                                <Paper
                                  elevation={0}
                                  sx={{
                                    p: 1.5,
                                    border: `1px solid ${alpha(theme.palette.divider, 0.3)}`,
                                    borderLeft: `3px solid ${theme.palette.secondary.main}`,
                                    transition: 'all 0.2s',
                                    '&:hover': {
                                      transform: 'translateY(-2px)',
                                      boxShadow: `0 4px 12px ${alpha(theme.palette.secondary.main, 0.1)}`,
                                      borderLeftColor: theme.palette.primary.main,
                                    }
                                  }}
                                >
                                  <Typography 
                                    variant="caption" 
                                    fontWeight="600" 
                                    color="secondary.main"
                                    sx={{ 
                                      display: 'block', 
                                      mb: 1,
                                      fontSize: '0.7rem',
                                      textTransform: 'uppercase',
                                      letterSpacing: 0.5
                                    }}
                                  >
                                    {mode}
                                  </Typography>
                                  <Stack spacing={0.5}>
                                    <Box display="flex" justifyContent="space-between" alignItems="center">
                                      <Typography variant="caption" color="text.secondary" fontSize="0.65rem">
                                        Bets
                                      </Typography>
                                      <Chip 
                                        label={data.bets} 
                                        size="small" 
                                        sx={{ 
                                          height: 18, 
                                          fontSize: '0.65rem',
                                          fontWeight: 600,
                                          bgcolor: alpha(theme.palette.secondary.main, 0.1),
                                          color: 'secondary.main'
                                        }} 
                                      />
                                    </Box>
                                    <Box display="flex" justifyContent="space-between" alignItems="center">
                                      <Typography variant="caption" color="text.secondary" fontSize="0.65rem">
                                        Amount
                                      </Typography>
                                      <Typography variant="caption" fontWeight="700" color="success.main" fontSize="0.7rem">
                                        {formatCurrency(data.amount)}
                                      </Typography>
                                    </Box>
                                    <Box display="flex" justifyContent="space-between" alignItems="center">
                                      <Typography variant="caption" color="text.secondary" fontSize="0.65rem">
                                        Wins
                                      </Typography>
                                      <Chip 
                                        label={data.wins} 
                                        size="small" 
                                        color={data.wins > 0 ? "error" : "default"}
                                        sx={{ 
                                          height: 18, 
                                          fontSize: '0.65rem',
                                          fontWeight: 600
                                        }} 
                                      />
                                    </Box>
                                  </Stack>
                                </Paper>
                              </Grid>
                            ))}
                          </Grid>
                        </Box>
                      )}
                    </Box>
                  )
                ))
              )}
            </CardContent>
          </Card>
        )}

      </Container>
    </LocalizationProvider>
  );
};

export default Overview;
